export const BATCH_UPDATE_FREQUENCY_DAYS = 30;

const dayInMillis = 1000 * 60 * 60 * 24;

/**
 * theTradeDesk has a [limit](https://api.thetradedesk.com/v3/portal/data/doc/DataRatesOverview)
 * of 30 days for updating data rates of type `system`.
 *
 * Returns true if a running batch update exists within the given `batchUpdateFrequencyDays`.
 *
 * @param lastUpdated the last updated batch date
 * @param batchUpdateFrequencyDays brand frequency update limit days
 * @param now current date time (mostly for testing)
 */
export const systemPriceUpdatePeriodStillRunning = (lastUpdated: string | undefined,
                                                    batchUpdateFrequencyDays: number = BATCH_UPDATE_FREQUENCY_DAYS,
                                                    now: Date = new Date()): boolean => {
  if (!lastUpdated) {
    return false;
  }

  const lastUpdatedDate = new Date(lastUpdated);

  const threshold = new Date(lastUpdatedDate.getTime() + (batchUpdateFrequencyDays * dayInMillis));

  return now.getTime() <= threshold.getTime();
};

/**
 * Calculates next possible date.
 *
 * @param lastUpdated
 * @param batchUpdateFrequencyDays
 */
export const nextSystemPriceUpdateAt = (lastUpdated: string | undefined,
                                        batchUpdateFrequencyDays: number = BATCH_UPDATE_FREQUENCY_DAYS): Date => {
  const now = new Date();

  if (!lastUpdated) {
    return now;
  }

  const lastUpdatedDate = new Date(lastUpdated);

  return new Date(lastUpdatedDate.getTime() + (batchUpdateFrequencyDays * dayInMillis));
};

/**
 * Creates an object with all information about a possible warning.
 *
 * @param lastUpdated
 */
export const ttdSystemPriceWarning = (lastUpdated?: string): TtdSystemPriceWarning => {
  const result: TtdSystemPriceWarning = {
    running: systemPriceUpdatePeriodStillRunning(lastUpdated),
    lastUpdated
  };
  if (result.running) {
    result.text = `Next regular system data rate update: ${nextSystemPriceUpdateAt(lastUpdated).toLocaleString()}`;
  }
  return result;
};

export interface TtdSystemPriceWarning {
  /**
   * The given last updated date.
   */
  lastUpdated?: string;

  /**
   * Indicates if currently an update is within a 30 days period.
   */
  running: boolean;

  /**
   * A warning text if the `running` is true.
   */
  text?: string;
}
